import { GrandstandButton } from '@/components/Buttons'
import { GrandstandInput } from '@/components/Input'
import { HeadingLabel } from '@/styles/fonts'
import { GrandstandIcon } from '@grandstand-web/bally-web-shared/src/components/GrandstandIcon'
import { UserServiceContext } from '@grandstand-web/bally-web-shared/src/services/user/UserService'
import { isValidZipcode } from '@grandstand-web/bally-web-shared/src/utils/stringUtils'
import { useContext, useState } from 'react'
import styled from 'styled-components'
import PasswordStrength from '../widgets/PasswordStrength'

type UpdateFieldProps = {
  onSuccess: (fieldName: string, newValue: string) => void
  onCancel: () => void
  fieldName: string
  currentFieldData: string
}

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
`

const Header = styled(HeadingLabel)`
  border-bottom: 1px solid var(--divider-canvas);
  padding-bottom: 25px;
  padding-left: 25px;
  padding-top: 25px;
`

const UpdateFieldForm = styled.form`
  margin: 0 auto;
  margin-top: 20px;
  @media (min-width: 768px) {
    width: 480px;
  }
  div {
    width: 100%;
  }
`

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  position: relative;
  text-align: left;
  div:nth-child(2) {
    margin-top: 15px;
  }
`

const UpdateFieldInput = styled(GrandstandInput)`
  width: 100%;
`

const SubmitButton = styled(GrandstandButton)`
  width: 100%;
  margin-top: 20px;
`

const BackIcon = styled.div`
  cursor: pointer;
  div {
    float: left;
    top: 3px;
    right: 10px;
    position: relative;
  }
`

const ArrowIcon = styled(GrandstandIcon)``

const UpdateField = ({ onSuccess, onCancel, fieldName, currentFieldData }: UpdateFieldProps) => {
  const [newValue, setNewValue] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isButtonDisabled, setIsButtonDisabled] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const { updateZipcode, isInMarket } = useContext(UserServiceContext)

  const onUpdateField = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (fieldName === 'Password' && newValue !== confirmPassword) {
      alert('Passwords do not match. Please confirm the new password correctly.')
      return
    }

    setIsLoading(true)
    try {
      await onSuccess(fieldName, newValue)
      setIsLoading(false)
    } catch (error) {
      console.error('Error updating field:', error)
      setIsLoading(false)
    }
  }

  const onChangeValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (fieldName === 'Zip Code') {
      if (isValidZipcode(e.target.value)) {
        updateZipcode(e.target.value)
      }
    }
    setNewValue(e.target.value)
    setIsButtonDisabled(!e.target.value)
  }

  const onConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(e.target.value)
  }

  const handleCancel = () => {
    const form: HTMLFormElement | null = document.getElementById('update-field-form') as HTMLFormElement | null
    form?.reset() // Resetting the form assures that browsers won't ask to update the password
    if (fieldName === 'Zip Code') {
      // Explanation: the isInMarket calculation currently requires changing the user session, so we need to reset it
      updateZipcode(currentFieldData)
    }
    onCancel()
  }

  return (
    <MainContent>
      <Header fontSize="3">
        <BackIcon onClick={() => handleCancel()}>
          <ArrowIcon icon="arrow_left" /> Update {fieldName}
        </BackIcon>
      </Header>
      <UpdateFieldForm onSubmit={onUpdateField} id="update-field-form">
        <FormField>
          {fieldName === 'Name' && (
            <UpdateFieldInput
              type="text"
              id={fieldName}
              value={newValue}
              name={fieldName}
              label="Name"
              onChange={onChangeValue}
              placeholder={currentFieldData}
              required
            />
          )}
          {fieldName === 'Password' && (
            <>
              <UpdateFieldInput
                type="password"
                id={fieldName}
                value={newValue}
                name={fieldName}
                onChange={onChangeValue}
                placeholder={currentFieldData}
                required
              />
              <UpdateFieldInput
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                name="confirmPassword"
                onChange={onConfirmPasswordChange}
                placeholder="Confirm new password"
                required
              />
              <PasswordStrength password={newValue} />
            </>
          )}
          {fieldName === 'Zip Code' && (
            <>
              <UpdateFieldInput
                type="number"
                id={fieldName}
                value={newValue}
                name={fieldName}
                onChange={onChangeValue}
                label="Zip Code"
                placeholder={currentFieldData}
                state={isInMarket ? 'default' : 'error'}
                required
              />
              {!isInMarket && (
                <div style={{ color: 'var(--error)' }}>FanDuel Sports Network is not available in this area.</div>
              )}
            </>
          )}
        </FormField>
        <SubmitButton
          buttonStyle="primary"
          buttonSize="sm"
          type="submit"
          disabled={isButtonDisabled}
          loading={isLoading}
        >
          UPDATE
        </SubmitButton>
      </UpdateFieldForm>
    </MainContent>
  )
}
export default UpdateField
